import React, { useCallback, Fragment, useEffect, useState } from 'react';
import { Button, Col, Row, UncontrolledButtonDropdown, DropdownMenu, DropdownItem, DropdownToggle } from 'reactstrap';
import { IoIosLink, IoIosCode, IoIosMail, IoLogoFacebook, IoLogoTwitter, IoLogoLinkedin, IoMdGlobe } from 'react-icons/io';
import Strings from '../../translations/Translations';
import { copyTextToClipboard } from '../../helpers/CopyTextToClipboard';
import { toast } from 'react-toastify';
import { getShareOptions } from '../../helpers/GetShareOptions';
import PSO from '../../services/PSO';
import { useAuth } from '../../Context/Auth/AuthContext';
import RemixExport from '../../modules/campaign/manage/components/RemixExport';

const PUBLIC_VIDEOS_URL = process.env.REACT_APP_PUBLIC_VIDEOS;
const PUBLIC_VIDEOS_URL_POPFANS = process.env.REACT_APP_PUBLIC_VIDEOS_POPFANS;
const SHARE_OPTIONS = getShareOptions();
const REMIX_URL = process.env.REACT_APP_PZ_REMIX_URL;
const EMBEDDED_PLUGIN_URL = process.env.REACT_APP_EMBEDDED_PLUGIN_FRAME;

export default function ShareComponent(props) {
  const { currentIt } = props;
  const { authenticated } = useAuth();
  const [remixPars, setRemixPars] = useState();
  const socialIconsColor = '#000000';

  const getUrl = useCallback(() => {
    let url = '';
    if (remixPars && remixPars.tid) {
      const { tid } = remixPars;
      let videosURL = PUBLIC_VIDEOS_URL;

      if (remixPars.mvon || remixPars.apmon) {
        videosURL = PUBLIC_VIDEOS_URL_POPFANS;
      }

      url = `${videosURL}+${tid}`;
    }
    return url;
  }, [remixPars]);

  const onClickOpenLink = useCallback(() => {
    if (remixPars) {
      window.open(getUrl(), '_blank');
    }
  }, [remixPars, getUrl]);

  const onClickCopyLink = useCallback(async () => {
    if (remixPars) {
      await copyTextToClipboard(getUrl());
      toast.success(Strings('url_copied_to_clipboard'));
    }
  }, [remixPars, getUrl]);

  const onClickEmbededCode = useCallback(async () => {
    if (remixPars) {
      const { rid } = remixPars;
      let iframe = `<iframe id="${rid}" width="640" height="360" src="${EMBEDDED_PLUGIN_URL}?remixid=${rid}&nooverbottomstartag?noovertopstartag?refreshonrestartbtn" title="Pepblast Player" frameborder="0" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowfullscreen></iframe>`;
      await copyTextToClipboard(iframe);
      toast.success(Strings('url_copied_to_clipboard'));
    }
  }, [remixPars]);

  const onClickEmail = useCallback(() => {
    if (remixPars) {
      const { rti, rown } = remixPars;
      const subject = encodeURIComponent(`Remix: ${rti}`);
      const description = encodeURIComponent(`Check this remix out: \n\n ${getUrl()} \n\n Video created by ${rown} at: ${REMIX_URL}`);
      const mail = `mailto:?subject=${subject}&body=${description}`;
      window.open(mail, '_blank');
    }
  }, [remixPars, getUrl]);

  const onClickSocialFacebook = useCallback(() => {
    if (remixPars) {
      const url = `https://www.facebook.com/sharer/sharer.php?u=${getUrl()}`;
      window.open(url, '_blank');
    }
  }, [remixPars, getUrl]);

  const onClickSocialTwitter = useCallback(() => {
    if (remixPars) {
      const url = `https://twitter.com/intent/tweet?hashtags=PepBlast&text=${remixPars.rti} (by ${remixPars.rown})&tw_p=tweetbutton&url=${getUrl()}`;
      window.open(url, '_blank');
    }
  }, [remixPars, getUrl]);

  const onClickSocialLinkedin = useCallback(() => {
    if (remixPars) {
      console.log('onClickSocialLinkedin');
    }
  }, [remixPars]);

  const getRemixPars = useCallback(async (rid) => {
    try {
      const response = await PSO.getRemixPars({
        un: authenticated.un,
        al: authenticated.al,
        rid: rid
      });
      if (response.result === 'success' && response.its && response.its.length > 0) {
        setRemixPars(response.its[0]);
      }
    } catch (err) {
      console.error(Strings('general_error'));
    }
  }, [setRemixPars, authenticated]);

  useEffect(() => {
    getRemixPars(currentIt.rid);
  }, [currentIt, getRemixPars]);

  return (
    <Row>
      <Col>

        <Button
          color="primary"
          className="btn-shadow mr-2 mb-2"
          onClick={onClickOpenLink}
        >
          <IoMdGlobe color="#ffffff" />
          <span className="ml-1">
            {Strings('open')}
          </span>
        </Button>

        <Button
          color="primary"
          className="btn-shadow mr-2 mb-2"
          onClick={onClickCopyLink}
        >
          <IoIosLink color="#ffffff" />
          <span className="ml-1">
            {Strings('copy_link')}
          </span>
        </Button>

        {SHARE_OPTIONS.EMBED_CODE && (
          <Fragment>
            <Button
              color="primary"
              className="btn-shadow mr-2 mb-2"
              onClick={onClickEmbededCode}
            >
              <IoIosCode color="#ffffff" />
              <span className="ml-1">
                {Strings('embeded_code')}
              </span>
            </Button>
          </Fragment>
        )}

        {SHARE_OPTIONS.EMAIL && (
          <Button
            color="primary"
            className="btn-shadow mr-2 mb-2"
            onClick={onClickEmail}
          >
            <IoIosMail color="#ffffff" />
            <span className="ml-1">
              {Strings('email')}
            </span>
          </Button>
        )}

        <UncontrolledButtonDropdown>
          <DropdownToggle caret className="mr-2 mb-2" color="primary">
            {Strings('social_share')}
          </DropdownToggle>
          <DropdownMenu>
            {SHARE_OPTIONS.FACEBOOK && (
              <DropdownItem
                onClick={onClickSocialFacebook}
              >
                <span className="mr-2">{Strings('facebook')}</span> <IoLogoFacebook color={socialIconsColor} size={20} />
              </DropdownItem>
            )}
            {SHARE_OPTIONS.TWITTER && (
              <DropdownItem
                onClick={onClickSocialTwitter}
              >
                <span className="mr-2">{Strings('twitter')}</span> <IoLogoTwitter color={socialIconsColor} size={20} />
              </DropdownItem>
            )}
            {SHARE_OPTIONS.LINKEDIN && (
              <DropdownItem
                onClick={onClickSocialLinkedin}
              >
                <span className="mr-2">{Strings('linkedin')}</span> <IoLogoLinkedin color={socialIconsColor} size={20} />
              </DropdownItem>
            )}
          </DropdownMenu>
        </UncontrolledButtonDropdown>

        <RemixExport label={Strings('export_to_mp4')} remixPars={currentIt} className='btn-shadow mr-2 mb-2' />

      </Col>
    </Row>
  );
}
